import { FontWeights } from "@flat-ui";

/**
 * Type for more advanced CSS selectors,
 * for example "{ path:not([fill='none'])": { fill: "FFFFFF" } }
 */

/** Default font size for titles */
export const DEFAULT_TITLE_FONT_SIZE = "16px";

/** Default font size for input elements like text fields and selects */
export const DEFAULT_INPUT_FONT_SIZE = "14px";

/** Default font size for normal text */
export const DEFAULT_TEXT_FONT_SIZE = "12px";

/** Default font weight for normal text */
export const DEFAULT_TEXT_FONT_WEIGHT = FontWeights.Regular;

/** Default font weight for bold text */
export const DEFAULT_BOLD_TEXT_FONT_WEIGHT = FontWeights.SemiBold;

/** Opacity in a 0-1 range to show when a element is disabled. */
export const DISABLED_OPACITY = 0.5;

/** Sx styles to render a MUI Box/Link without default anchor styling (no underline, inherited color). */
export const unstyledLinkSx = {
  display: "block",
  cursor: "pointer",
  textDecoration: "none",
  color: "inherit"
};

/**
 * This breakpoint does not exist in MUI but we will use it as a new size,
 * since it is the most common size for our customers.
 */
export const XXL_BREAKPOINT = 1920;

/**
 * This breakpoint does not exist in MUI but we will use it as a new size,
 * since it is a size our customers use.
 */
export const XXXL_BREAKPOINT = 2560;

/**
 * The palette's divider colour, as a raw CSS variable.
 *
 * `theme.vars.palette.divider` is the same string, but this module is imported by the theme
 * itself and so cannot import it back. MUI derives the variable name from the palette path and
 * the default `mui` prefix, so this tracks `palette.divider` across both colour schemes.
 */
const DIVIDER_COLOR = "var(--mui-palette-divider)";
export const CommonStyles = {
  Borders: {
    divider: `1px solid ${DIVIDER_COLOR}`
  }
};

/** Font family to be used to display content normal or bold */
export const DEFAULT_FONT_FAMILY = '"Open Sans", sans-serif';

/** Font family to be used to display content in italics. Marked as important to replace the default one */
export const DEFAULT_FONT_FAMILY_ITALIC = "OpenSans-Italic !important";

/**
 * Defines the custom breakpoints for the dashboard.
 */
export const PAGE_BREAKPOINTS = {
  xs: 0,
  sm: 600,
  md: 900,
  lg: 1200,
  xl: 1536,
  xxl: XXL_BREAKPOINT
};

/** Extended breakpoints to include the new sizes */

/**
 * Defines the custom breakpoints for the dashboard, including the new sizes.
 */
export const PAGE_BREAKPOINTS_EXTENDED = {
  ...PAGE_BREAKPOINTS,
  xxxl: XXXL_BREAKPOINT
};

/**
 * Horizontal padding of the page content in MUI spacing units (24px), the same at every page size.
 */
export const PAGE_X_PADDING = 3;

/**
 * Maximum width of the content below the app bar, the same at every page size: the smallest XXL
 * screen. Beyond it the content is centred and the remaining space is split between both sides.
 */
export const CONTENT_MAX_WIDTH = `${XXL_BREAKPOINT}px`;

/**
 * Needed Css properties to wrap text and show an
 * ellipsis when overflowing text.
 */
export const withEllipsis = {
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap"
};

/**
 * Needed Css properties to wrap text and show an
 * ellipsis when overflowing text.
 * It serves as base to break in multiple lines.
 * Use WebkitLineClamp to define the maximum lines ot be shown.
 */
const withEllipsisLines = {
  overflow: "hidden",
  textOverflow: "ellipsis",
  // !: Do not use ...withEllipsis
  // because we don't need whiteSpace: "nowrap" in order to
  // break the line in two rows.
  wordBreak: "break-word",
  lineHeight: "20px",
  // These properties allow to show an ellipsis on two lines
  // Unfortunately it is only allowed in webkit because there's
  // no native support from CSS for this, only for one line.
  display: "-webkit-box",
  WebkitBoxOrient: "vertical"
};

/**
 * Needed Css properties to wrap text and show an
 * ellipsis when overflowing text.
 * It breaks the lines and shows a maximum of the provided lines
 */
function getEllipsisLines({
  lines
}) {
  return {
    ...withEllipsisLines,
    WebkitLineClamp: lines
  };
}

/** Css properties to show an ellipsis on two lines */
export const withEllipsisTwoLines = getEllipsisLines({
  lines: 2
});

/** Css properties to show an ellipsis on three line */
export const withEllipsisThreeLines = getEllipsisLines({
  lines: 3
});

/** Css properties to show an ellipsis on four lines */
export const withEllipsisFourLines = getEllipsisLines({
  lines: 4
});

/**
 * Needed Css properties for all links in the
 * user menu. They don't have an underline
 * on hover or active, and are colored to blue on hover.
 */
export const userMenuLink = {
  textDecoration: "none",
  color: "text.primary",
  "&:hover": {
    color: "primary.main",
    cursor: "pointer"
  },
  "&::active": {
    color: "text.primary"
  },
  "&:hover,:active": {
    textDecoration: "none"
  }
};
export function getSvgColoredIconCss(color) {
  /* eslint-disable @typescript-eslint/naming-convention -- The namings are CSS selectors */
  return {
    "path:not([fill='none'])": {
      fill: color
    },
    "rect:not([fill='none'])": {
      fill: color
    },
    "circle:not([fill='none'])": {
      fill: color
    },
    "ellipse:not([fill='none'])": {
      fill: color
    },
    "line:not([fill='none'])": {
      fill: color
    },
    "polyline:not([fill='none'])": {
      fill: color
    },
    "polygon:not([fill='none'])": {
      fill: color
    }
  };
  /* eslint-enable */
}

/** Sx style for placeholders */
export const placeholderItalicSx = {
  fontSize: "14px",
  fontFamily: DEFAULT_FONT_FAMILY_ITALIC,
  color: "text.disabled",
  opacity: 1
};

/** Sx style to have an italic placeholder in any input element  */
export const inputPlaceholderItalicSx = {
  "& .MuiInputBase-input::placeholder": {
    ...placeholderItalicSx
  }
};

/** Sx style to have the correct error color for a text field border */
const textfieldFieldsetSx = {
  "& .MuiOutlinedInput-root": {
    "&.Mui-error fieldset": {
      borderColor: "error.main"
    }
  }
};

/** Style override for text fields */
export const textfieldSx = {
  ...inputPlaceholderItalicSx,
  ...textfieldFieldsetSx
};

/**
 * Hides the underline of an underlined flat-ui TextField, for the inline-edit fields that draw
 * their own bottom border or none at all.
 *
 * Each state is restated because flat-ui styles them at three class names deep; a rule written
 * only against the idle state would lose to them on hover and focus.
 */
export const noInputUnderlineSx = {
  "&.MuiInput-underline": {
    "&:before, &:after": {
      borderBottom: "none"
    }
  },
  "&:hover, &.Mui-focused, &.Mui-disabled": {
    "&.MuiInput-underline": {
      "&:before, &:after": {
        borderBottom: "none"
      }
    }
  }
};

/** Default CSS style to apply when the button is disabled and it is hovered. */
export const hoverDisabledStyle = {
  backgroundColor: "transparent",
  cursor: "default"
};

/** Default CSS style to apply when the button is disabled and it is clicked. */
export const clickDisabledStyle = {
  backgroundColor: "transparent",
  cursor: "default"
};

/** Default gray border underneath the tabs */
export const DEFAULT_TAB_UNDERLINE = `0 1px 0 0 ${DIVIDER_COLOR}`;

/** Do not show a box shadow */
export const TAB_WITH_NO_UNDERLINE = "none";

/** Default width for side panels like the FaroTableSidepanel */
export const DEFAULT_SIDEPANEL_WIDTH = 320;

/** The width of the thumbnail image in pixels */
export const THUMBNAIL_IMAGE_WIDTH = 70;

/** The height of the thumbnail image in pixels */
export const THUMBNAIL_IMAGE_HEIGHT = 50;

/** Size in pixels of an action button */
export const ACTION_BTN_SIZE = "30px";

/** Size in pixels of an action button icon */
export const ACTION_ICON_SIZE = "18px";